import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "firewatch:access_token";

export const api = axios.create({
  baseURL: API,
  timeout: 30000,
});

// Attach the access token (if any) on every request
api.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Expired/invalid token: drop it and tell AuthContext so the UI flips to
// logged-out immediately (no manual logout needed before re-login).
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && localStorage.getItem(TOKEN_KEY)) {
      localStorage.removeItem(TOKEN_KEY);
      window.dispatchEvent(new Event("fw:auth-expired"));
    }
    return Promise.reject(err);
  }
);

export const fetchCounties = () => api.get("/counties").then((r) => r.data);

// "washington" is the statewide pseudo-county: omit the filter (or use "all"
// where the endpoint defaults to a county) so responses cover the whole state.
const nc = (c) => (c === "washington" ? undefined : c);

export const fetchIncidents = (params = {}) => {
  const p = { ...params };
  if (p.county === "washington") p.county = "all";
  return api.get("/incidents", { params: p }).then((r) => r.data);
};

export const fetchIncident = (id) => api.get(`/incidents/${id}`).then((r) => r.data);

export const fetchAlerts = (county) =>
  api.get("/alerts", { params: nc(county) ? { county: nc(county) } : {} }).then((r) => r.data);

export const fetchRoadAlerts = (county) =>
  api.get("/road-alerts", { params: nc(county) ? { county: nc(county) } : {} }).then((r) => r.data);

export const fetchMountainPasses = (county) =>
  api.get("/mountain-passes", { params: nc(county) ? { county: nc(county) } : {} }).then((r) => r.data);

export const fetchCameras = (county) =>
  api.get("/cameras", { params: nc(county) ? { county: nc(county) } : {} }).then((r) => r.data);

export const fetchIncidentCameras = (id, limit = 3) =>
  api.get(`/incidents/${id}/cameras`, { params: { limit } }).then((r) => r.data);

export const fetchStats = (county) =>
  api.get("/stats", { params: county ? { county: county === "washington" ? "all" : county } : {} }).then((r) => r.data);

export const fetchUpdates = (id) =>
  api.get(`/incidents/${id}/updates`).then((r) => r.data);

export const postUpdate = (id, payload) =>
  api.post(`/incidents/${id}/updates`, payload).then((r) => r.data);

export const uploadFieldImage = (file, coords) => {
  const fd = new FormData();
  fd.append("file", file);
  if (coords?.lat != null && coords?.lon != null) {
    fd.append("lat", coords.lat);
    fd.append("lon", coords.lon);
  }
  return api.post("/uploads/field-image", fd, { timeout: 120000 }).then((r) => r.data);
};

export const fetchIncidentPhotos = (id) =>
  api.get(`/incidents/${id}/photos`).then((r) => r.data);

export const fetchFieldPhotos = () =>
  api.get("/field-photos").then((r) => r.data);

export const fetchWeather = (lat, lon) =>
  api.get("/weather", { params: { lat, lon } }).then((r) => r.data);

export const fetchWeatherOutlook = (lat, lon) =>
  api.get("/weather/outlook", { params: { lat, lon } }).then((r) => r.data);

export const fetchDebrisFlowRisk = (county) =>
  api.get("/debris-flow-risk", { params: nc(county) ? { county: nc(county) } : {} }).then((r) => r.data);

export const fetchStreamGauges = () =>
  api.get("/stream-gauges").then((r) => r.data);

export const fetchDownstreamPaths = () =>
  api.get("/downstream-paths").then((r) => r.data);

export const fileUrl = (id) => `${API}/files/${id}`;

export const fetchSitReport = () =>
  api.get("/sit-report/washington").then((r) => r.data);

export const fetchNifcBriefing = () =>
  api.get("/nifc-briefing").then((r) => r.data);

const VIDEO_CHUNK = 4 * 1024 * 1024;

export const uploadVideoChunked = async (file, onProgress) => {
  const { data: init } = await api.post("/uploads/video/init");
  const total = Math.ceil(file.size / VIDEO_CHUNK);
  for (let i = 0; i < total; i += 1) {
    const fd = new FormData();
    fd.append("upload_id", init.upload_id);
    fd.append("index", String(i));
    fd.append("chunk", file.slice(i * VIDEO_CHUNK, (i + 1) * VIDEO_CHUNK));
    await api.post("/uploads/video/chunk", fd);
    onProgress?.(Math.round(((i + 1) / (total + 1)) * 100));
  }
  const fd = new FormData();
  fd.append("upload_id", init.upload_id);
  fd.append("filename", file.name || "");
  fd.append("content_type", file.type || "video/mp4");
  const { data } = await api.post("/uploads/video/complete", fd);
  onProgress?.(100);
  return data;
};

export const refreshNow = () => api.post("/refresh").then((r) => r.data);

export const fetchEvacuationZones = (county) =>
  api.get("/evacuation-zones", { params: nc(county) ? { county: nc(county) } : {} }).then((r) => r.data);

export const fetchBcFires = () => api.get("/bc/fires").then((r) => r.data);
export const fetchBcEvacs = () => api.get("/bc/evacs").then((r) => r.data);

export const fetchAcresHistory = (id, nifcId, days) =>
  api
    .get(`/incidents/${id}/acres-history`, {
      params: { ...(nifcId ? { nifc_id: nifcId } : {}), ...(days ? { days } : {}) },
    })
    .then((r) => r.data);

export const fetchAcresHistoryBulk = (days) =>
  api.get("/acres-history-bulk", { params: days ? { days } : {} }).then((r) => r.data);

export const fetchGrowthEstimate = (id) =>
  api.get(`/incidents/${id}/growth-estimate`).then((r) => r.data);

export const fetchSpreadAnalysis = (id, nifcId) =>
  api
    .get(`/incidents/${id}/spread-analysis`, { params: nifcId ? { nifc_id: nifcId } : {} })
    .then((r) => r.data);

export const fetchNwccBrief = () =>
  api.get("/nwcc-brief").then((r) => r.data);

export const trackDonateClick = (source) => {
  api.post("/metrics/donate-click", { source }).catch(() => {});
};

export const fetchDonateClicks = () =>
  api.get("/metrics/donate-clicks").then((r) => r.data);

export const fetchNifcIncidents = ({ county, type_category, active_only } = {}) => {
  const params = {};
  if (nc(county)) params.county = nc(county);
  if (type_category) params.type_category = type_category;
  if (active_only) params.active_only = true;
  return api.get("/nifc-incidents", { params }).then((r) => r.data);
};

export const fetchViirsHotspots = ({ county, hours, confidence, daynight } = {}) => {
  const params = {};
  if (nc(county)) params.county = nc(county);
  if (hours) params.hours = hours;
  if (confidence) params.confidence = confidence;
  if (daynight) params.daynight = daynight;
  return api.get("/viirs-hotspots", { params }).then((r) => r.data);
};

export const fetchDnrBurnRequests = () =>
  api.get("/dnr-burn-requests").then((r) => r.data);

export const fetchRxBurnUnits = () =>
  api.get("/rx-burn-units").then((r) => r.data);

export const fetchDnrFires = ({ county, days = 14, active_only = false } = {}) => {
  const params = { days };
  if (nc(county)) params.county = nc(county);
  if (active_only) params.active_only = true;
  return api.get("/dnr-fires", { params }).then((r) => r.data);
};

export const fetchBurnBans = (county) =>
  api.get("/burn-bans", { params: { county } }).then((r) => r.data);

export const fetchEvacAlerts = ({ county, active_only = true, include_historical = false } = {}) => {
  const params = { active_only };
  if (include_historical) params.include_historical = true;
  if (nc(county)) params.county = nc(county);
  return api.get("/evac-alerts", { params }).then((r) => r.data);
};

export const fetchRoadClosures = (county) =>
  api.get("/road-closures", { params: nc(county) ? { county: nc(county) } : {} }).then((r) => r.data);

export const fetchAircraft = (county) =>
  api.get("/aircraft", { params: { county } }).then((r) => r.data);

export const fetchSmoke = () => api.get("/smoke").then((r) => r.data);
export const fetchSponsors = () => api.get("/sponsors").then((r) => r.data);
export const trackSponsor = (id, kind, surface) =>
  api.post(`/sponsors/${id}/track`, { kind, surface }).catch(() => {});
export const fetchLightning = (minutes = 60, hours = null) =>
  api
    .get("/lightning", { params: hours && hours > 1 ? { hours } : { minutes } })
    .then((r) => r.data);

export const fetchWindField = (county) =>
  api.get("/wind-field", { params: nc(county) ? { county: nc(county) } : {} }).then((r) => r.data);

export const fetchWindFieldBBox = (bbox) =>
  api.get("/wind-field", { params: bbox }).then((r) => r.data);

export const fetchWxZones = () => api.get("/wx-zones").then((r) => r.data);

export const fetchEvacRoutes = (county) =>
  api.get("/evac-routes", { params: nc(county) ? { county: nc(county) } : {} }).then((r) => r.data);

export const fetchAqi = () => api.get("/aqi").then((r) => r.data);

export const createEvacAlert = (payload) =>
  api.post("/evac-alerts", payload).then((r) => r.data);

export const updateEvacAlert = (id, payload) =>
  api.patch(`/evac-alerts/${id}`, payload).then((r) => r.data);

export const fetchEvacZoneHistory = (id) =>
  api.get(`/evac-alerts/${id}/history`).then((r) => r.data);

export const fetchInfoPins = (county) =>
  api.get("/info-pins", { params: nc(county) ? { county: nc(county) } : {} }).then((r) => r.data);

export const createInfoPin = (payload) =>
  api.post("/info-pins", payload).then((r) => r.data);

export const updateInfoPin = (id, payload) =>
  api.patch(`/info-pins/${id}`, payload).then((r) => r.data);

export const deleteInfoPin = (id) =>
  api.delete(`/info-pins/${id}`).then((r) => r.data);

export const fetchHistoricFires = ({ county, years = 3, min_acres = 0 } = {}) => {
  const params = { years };
  if (nc(county)) params.county = nc(county);
  if (min_acres) params.min_acres = min_acres;
  return api.get("/historic-fires", { params }).then((r) => r.data);
};

export const fetchHistoricStats = ({ county, years = 3 } = {}) => {
  const params = { years };
  if (nc(county)) params.county = nc(county);
  return api.get("/historic-fires/stats", { params }).then((r) => r.data);
};

export const fetchEscalations = ({ days = 7, county } = {}) => {
  const params = { days };
  if (nc(county)) params.county = nc(county);
  return api.get("/incidents/escalations", { params }).then((r) => r.data);
};

export const fetchCaughtStats = ({ days = 7 } = {}) =>
  api.get("/stats/caught", { params: { days } }).then((r) => r.data);
